import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

// Suppress known Supabase auth errors from console to avoid noise
if (typeof window !== 'undefined' && !(window as any).__sb_console_patched__) {
  (window as any).__sb_console_patched__ = true;
  const origError = console.error.bind(console);
  console.error = (...args: any[]) => {
    const msg = args[0]?.message ?? (typeof args[0] === 'string' ? args[0] : '');
    const isKnownAuthError =
      msg.includes('Request rate limit reached') ||
      msg.includes('refresh_token_not_found') ||
      msg.includes('Refresh Token Not Found') ||
      msg.includes('Invalid Refresh Token') ||
      msg.includes('Token has expired');
    if (isKnownAuthError) return; // suppress silently
    origError(...args);
  };
}

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined' ? [] :
  document.cookie.split(';').filter(Boolean).map((c) => {
    const [name, ...rest] = c.trim().split('=');
    return { name: name.trim(), value: decodeURIComponent(rest.join('=')) };
  }).filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch { return []; }
};

const setCookie = (name: string, value: string, options?: any) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
};

const getToken = () =>
  (canUseCookies() ? fromCookies() : fromStorage())
    .find((c) => c.name.includes('auth-token'))?.value ?? null;

if (typeof window !== 'undefined' && !(window as any).__sb_patched__) {
  (window as any).__sb_patched__ = true;
  const orig = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const token = getToken();
    const url = typeof input === 'string' ? input
      : input instanceof URL ? input.href
      : (input as Request).url;
    // Only inject token for same-origin requests (not Supabase API calls — they use their own auth headers)
    const isSameOrigin = url.startsWith('/') || (typeof window !== 'undefined' && url.startsWith(window.location.origin));
    const isSupabaseUrl = typeof process !== 'undefined' && url.includes(process.env.NEXT_PUBLIC_SUPABASE_URL ?? '__none__');
    if (token && isSameOrigin && !isSupabaseUrl) {
      init = { ...(init || {}), headers: { ...(init?.headers || {}), 'x-sb-token': token } };
    }
    return orig(input, init);
  };
}

// Singleton instance — prevents multiple clients competing for the auth lock
let _client: ReturnType<typeof createBrowserClient> | null = null;

export function clearAuthStorage() {
  if (typeof window === 'undefined') return;
  // Clear from localStorage
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX) || k.startsWith('sb-'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
  // Clear from cookies
  document.cookie.split(';').forEach((c) => {
    const name = c.trim().split('=')[0];
    if (name.startsWith('sb-') || name.includes('auth-token')) {
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
    }
  });
  // Reset singleton so next createClient() gets a fresh instance
  _client = null;
}

/**
 * Returns true if the error is a known, non-critical Supabase auth error
 * that should be handled silently (invalid/missing refresh token, rate limit).
 */
export function isExpectedAuthError(err: unknown): boolean {
  if (!err) return false;
  const msg = (err as any)?.message ?? '';
  return (
    msg.toLowerCase().includes('refresh token') ||
    msg.toLowerCase().includes('invalid refresh') ||
    msg.toLowerCase().includes('rate limit') ||
    msg.toLowerCase().includes('too many requests') ||
    msg.toLowerCase().includes('token not found')
  );
}

export function createClient() {
  if (_client) return _client;
  _client = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        // Persist session in storage
        persistSession: true,
        // Detect session from URL (for OAuth/magic link callbacks)
        detectSessionInUrl: true,
        // Use PKCE flow for better security
        flowType: 'pkce',
        // Disable automatic token refresh — we handle it via onAuthStateChange
        // to avoid multiple concurrent refresh calls
        autoRefreshToken: true,
      },
      cookies: {
        getAll: () => canUseCookies() ? fromCookies() : fromStorage(),
        setAll(cookiesToSet: Array<{ name: string; value: string; options?: Record<string, unknown> }>) {
          if (typeof document === 'undefined') return;
          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) =>
              value ? setCookie(name, value, options)
                    : (document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`)
            );
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                value ? localStorage.setItem(`${PFX}${name}`, value)
                      : localStorage.removeItem(`${PFX}${name}`);
              } catch {}
              if (value) setCookie(name, value, options);
            });
          }
        },
      },
    }
  );
  return _client;
}
